import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UploadOldResult() {
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [oldResults, setOldResults] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedSem, setSelectedSem] = useState("");
  const [feeHeadId, setFeeHeadId] = useState("");
  const [defaultFeeAmount, setDefaultFeeAmount] = useState("");
  const [remarks, setRemarks] = useState("");

  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const userName = user?.user_name || user?.username || "system";
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const csvHeaders = [
    "roll_no",
    "sem_no",
    "code_no",
    "subject_cd",
    "int_mark",
    "sem_mark",
    "ttl_mark",
    "result",
    "grade",
    "update",
    "may",
    "period",
  ];

  // --------------------------
  // 1️⃣ LOAD EXISTING OLD RESULTS
  // --------------------------
  useEffect(() => {
    const loadOldResults = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/exam-old-results/${clientId}/${collegeId}/get-old-results`,
          { headers: { "Cache-Control": "no-cache" } }
        );
        const data = res.data.data || [];

        const rows = data.map((row, index) => ({
          id: index + 1,
          ...row,
        }));

        setOldResults(rows);
      } catch (err) {
        console.error("Failed to load old results", err);
      }
    };

    if (clientId && collegeId) loadOldResults();
  }, [clientId, collegeId, API_BASE_URL]);

  // --------------------------
  //  CSV TEMPLATE DOWNLOAD
  // --------------------------
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "old_results_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
  };

  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const data = results.data.map((row, index) => ({
          id: index + 1,
          ...row,
        }));
        setPreviewData(data);
      },
    });
  };

  const handleConfirm = async () => {
    if (previewData.length === 0) {
      alert("No data to upload!");
      return;
    }
    setUploading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/exam-old-results/${clientId}/${collegeId}/upload-old-results`,
        { results: previewData }
      );
      setMessage(res.data.message || "Upload successful!");
      setPreviewData([]);
      setCsvFile(null);
    } catch (err) {
      setMessage(err.response?.data?.message || "Upload failed");
    }
    setUploading(false);
  };

  // --------------------------
  //  3️⃣ GENERATE CSV FOR FAILED STUDENTS FOR SELECTED SEM
  //     --- now also insert tbl_fee_book records via API
  // --------------------------
  const handleGenerateAndInsertFeeBooks = async () => {
    if (!selectedSem) {
      alert("Please select a semester!");
      return;
    }
    if (!feeHeadId) {
      alert("Please enter Fee Head ID!");
      return;
    }

    const filtered = oldResults.filter(
      (d) => Number(d.sem_no) === Number(selectedSem) && d.result === "F"
    );

    if (filtered.length === 0) {
      alert("No failed students found for this semester!");
      return;
    }

    // group course codes by roll
    const groupedByRoll = {};
    const studentMap = {}; // we'll try to get student_id if available in oldResults rows
    filtered.forEach((item) => {
      if (!groupedByRoll[item.roll_no]) {
        groupedByRoll[item.roll_no] = [];
      }
      groupedByRoll[item.roll_no].push(item.code_no);
      if (!studentMap[item.roll_no]) {
        // if oldResults has student_id field, use it; else leave undefined
        studentMap[item.roll_no] = {
          student_id: item.student_id ? item.student_id : null,
        };
      }
    });

    // Build csvData entries (and fee_book records)
    // Note: BigInt values will be sent as strings and server will convert them to BigInt
    const nowIso = new Date().toISOString().slice(0, 19).replace("T", " ");
    const csvData = Object.keys(groupedByRoll).map((roll, i) => ({
      sno: i + 1,
      sem_no: selectedSem,
      roll_no: roll,
      course_codes: groupedByRoll[roll].join(","),
      // DB fields
      fee_head_id: String(feeHeadId), // send as string for safety (server converts)
      student_id: studentMap[roll]?.student_id ? String(studentMap[roll].student_id) : "0",
      student_rollno: roll,
      college_id: String(collegeId),
      fee_amount: String(defaultFeeAmount || "0"),
      remarks: remarks || "",
      workflow_status: "DRAFT",
      created_on: nowIso,
      created_by: userName,
      modified_on: nowIso,
      modified_by: userName,
    }));

    // send to backend to insert into tbl_fee_book
    setUploading(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/fee-book/${clientId}/${collegeId}/generate-fee-book`,
        { records: csvData }
      );

      if (res.data?.success) {
        setMessage("Fee book records inserted successfully — now downloading CSVs");
      } else {
        setMessage(res.data?.message || "Insert may have failed; check server logs");
      }
    } catch (err) {
      console.error("Insert error:", err);
      setMessage(err.response?.data?.message || "Insert failed");
      setUploading(false);
      return;
    }

    // If insertion succeeded (or even if not), trigger CSV downloads just like before
    for (let i = 1; i <= 5; i++) {
      const header = "sno,sem_no,roll_no,course_codes\n";
      const csvContent =
        header +
        csvData
          .map((row) => [row.sno, row.sem_no, row.roll_no, `"${row.course_codes}"`].join(","))
          .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `failed_sem_${selectedSem}_part${i}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    setUploading(false);
  };

  const columns = [
    { field: "id", headerName: "S.No", width: 80 },
    { field: "roll_no", headerName: "Roll No", width: 130 },
    { field: "sem_no", headerName: "Sem", width: 80 },
    { field: "subject_cd", headerName: "Subject Code", width: 150 },
    { field: "int_mark", headerName: "Int", width: 100 },
    { field: "sem_mark", headerName: "Sem", width: 100 },
    { field: "ttl_mark", headerName: "Total", width: 100 },
    { field: "result", headerName: "Result", width: 100 },
    { field: "grade", headerName: "Grade", width: 100 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Download Template */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{ backgroundColor: "#388e3c", "&:hover": { backgroundColor: "#2c6b2f" } }}
      >
        Download Template
      </Button>

      {/* Upload CSV */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{ ml: 2, backgroundColor: "#1976d2", "&:hover": { backgroundColor: "#115293" } }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{ ml: 2, backgroundColor: "#ff9800", "&:hover": { backgroundColor: "#c77700" } }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>

      <Button
        sx={{ ml: 2, backgroundColor: "#4caf50", "&:hover": { backgroundColor: "#357a38" } }}
        variant="contained"
        onClick={handleConfirm}
      >
        Confirm & Upload
      </Button>

      {uploading && <LinearProgress sx={{ mt: 2 }} />}

      {message && (
        <Alert
          severity={message.toLowerCase().includes("success") ? "success" : "info"}
          sx={{ mt: 2 }}
        >
          {message}
        </Alert>
      )}

      {/* Inputs for Fee Book creation */}
      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        <TextField
          label="Fee Head ID"
          value={feeHeadId}
          onChange={(e) => setFeeHeadId(e.target.value)}
          size="small"
        />
        <TextField
          label="Default Fee Amount"
          value={defaultFeeAmount}
          onChange={(e) => setDefaultFeeAmount(e.target.value)}
          size="small"
        />
        <TextField
          label="Remarks"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          size="small"
        />
      </Box>

      {/* 2️⃣ SEMESTER DROPDOWN */}
      <FormControl fullWidth sx={{ mt: 3 }}>
        <InputLabel>Select Semester</InputLabel>
        <Select
          value={selectedSem}
          label="Select Semester"
          onChange={(e) => setSelectedSem(e.target.value)}
        >
          {[...new Set(oldResults.map((r) => r.sem_no))].map((sem) => (
            <MenuItem key={sem} value={sem}>
              Semester {sem}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        sx={{ mt: 2, backgroundColor: "#000", "&:hover": { backgroundColor: "#333" } }}
        variant="contained"
        onClick={handleGenerateAndInsertFeeBooks}
      >
        Insert Fee Books & Download 5 Failed CSV Files
      </Button>

      {/* 1️⃣ EXISTING OLD RESULTS TABLE */}
      {oldResults.length > 0 && (
        <Box sx={{ mt: 3, height: 500 }}>
          <Typography variant="h6">Existing Old Results</Typography>
          <DataGrid rows={oldResults} columns={columns} pageSize={10} />
        </Box>
      )}

      {/* PREVIEW NEW CSV */}
      {previewData.length > 0 && (
        <Box sx={{ mt: 3, height: 500 }}>
          <Typography variant="h6">Preview Before Upload</Typography>
          <DataGrid rows={previewData} columns={columns} pageSize={10} />
        </Box>
      )}
    </Box>
  );
}
